import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip, Avatar, Grid, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FaTrophy, FaMedal, FaStar } from 'react-icons/fa';

export default function Appraisal() {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const auth = useSelector((state) => state?.loginState?.auth);
  const getCurrentFinancialYear = () => {
    return new Date().getFullYear().toString();
  };

  const getFinancialYears = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let y = 2022; y <= currentYear + 1; y++) {
      years.push(y.toString());
    }
    return years;
  };

  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [month, setMonth] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [scope, setScope] = useState("my_team");

  useEffect(() => {
    if (auth?.token) {
      fetchAppraisal();
    }
  }, [auth, financialYear, month, employeeName, scope]);

  const fetchAppraisal = async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/'}appraisal/?`;
      if (scope) url += `scope=${scope}&`;
      if (financialYear) url += `financial_year=${financialYear}&`;
      if (month) url += `month=${month}&`;
      if (employeeName) url += `employee_name=${employeeName}&`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      let fetchedData = res.data;
      if (fetchedData && fetchedData.results) {
        fetchedData = fetchedData.results;
      } else if (fetchedData && fetchedData.data) {
        fetchedData = fetchedData.data;
      }

      setAppraisals(Array.isArray(fetchedData) ? fetchedData : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (label) => {
    if (label === 'Excellent') return 'success';
    if (label === 'Good') return 'info';
    if (label === 'Average') return 'warning';
    return 'error';
  };

  const getRankIcon = (index) => {
    if (index === 0) return <FaTrophy color="#fbbf24" size={24} title="Rank 1" />;
    if (index === 1) return <FaMedal color="#94a3b8" size={22} title="Rank 2" />;
    if (index === 2) return <FaMedal color="#b45309" size={22} title="Rank 3" />;
    return <FaStar color="#cbd5e1" size={18} />;
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f8fafc' }}>
      <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Performance Appraisal
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Employee ranking based on task completion quality, timelines, and efficiency.
          </Typography>
        </Box>

        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            alignItems: { xs: 'stretch', md: 'center' }, 
            gap: 1.5, 
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            width: { xs: '100%', md: 'auto' }
          }}
        >
          <Box sx={{ width: { xs: '100%', md: '195px' }, flexShrink: 0 }}>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              style={{
                height: '40px',
                width: '100%',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1.5px solid #3b82f6',
                background: '#eff6ff',
                fontSize: '13px',
                fontWeight: 700,
                color: '#1d4ed8',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="my_team">👥 My Team (My Works)</option>
              <option value="all">🌐 All Employees (Org-wide)</option>
            </select>
          </Box>

          <Box sx={{ width: { xs: '100%', md: '125px' }, flexShrink: 0 }}>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              style={{
                height: '40px',
                width: '100%',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1e293b',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Years</option>
              {getFinancialYears().map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Box>

          <Box sx={{ width: { xs: '100%', md: '130px' }, flexShrink: 0 }}>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                height: '40px',
                width: '100%',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1e293b',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </Box>

          <Box sx={{ width: { xs: '100%', md: '200px' }, flexShrink: 0 }}>
            <input
              type="text"
              placeholder="Search Employee..."
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              style={{
                height: '40px',
                width: '100%',
                padding: '0 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1e293b',
                outline: 'none'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Top Performers Highlight */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {appraisals.slice(0, 3).map((perf, index) => (
          <Grid item xs={12} md={4} key={perf.employee_id}>
            <Box sx={{ p: 3, background: '#fff', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)', display: 'flex', alignItems: 'center', gap: 2, border: index === 0 ? '2px solid #fbbf24' : '1px solid #e2e8f0' }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: index === 0 ? '#fbbf24' : '#f1f5f9', color: index === 0 ? '#fff' : '#475569', fontWeight: 800 }}>
                {perf.employee_name.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{perf.employee_name}</Typography>
                  {getRankIcon(index)}
                </Box>
                <Typography sx={{ color: '#64748b', fontSize: 13 }}>Score: {perf.score}/{perf.max_score} ({perf.grade_percent}%)</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ background: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Completed</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>In Progress</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Pending / Overdue</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Grade</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appraisals.map((perf, index) => (
              <TableRow key={perf.employee_id} hover>
                <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>#{index + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{perf.employee_name.charAt(0)}</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{perf.employee_name}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{perf.employee_email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: '#10b981', fontWeight: 600 }}>{perf.completed}</TableCell>
                <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>{perf.in_progress}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 14, color: '#f59e0b', display: 'inline-block', mr: 1, fontWeight: 600 }}>{perf.pending}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#ef4444', display: 'inline-block', fontWeight: 600 }}>({perf.overdue})</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{perf.score} <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>/ {perf.max_score}</span></Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{perf.grade_percent}%</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={perf.grade_label} color={getGradeColor(perf.grade_label)} sx={{ fontWeight: 600, borderRadius: '6px' }} />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.location.href = `#/employee-timeline/${perf.employee_id}`}
                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                  >
                    View Timeline
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#3b82f6' }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                    <CircularProgress size={36} thickness={4} />
                    <Typography fontSize={13} color="#64748b" fontWeight={600}>Loading performance records...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : appraisals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                  No appraisal data found.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
